// LearnCore Frontend - 奖励卡片组件（儿童友好版本）

import React, { useState } from 'react';
import type { RewardTemplate, RedemptionStatus } from '../types/tasks-rewards';

interface RewardCardProps {
  reward: RewardTemplate;
  userBalance?: number;
  showUserDKP?: boolean;
  redemptionStatus?: RedemptionStatus;
  onRedeem?: (rewardId: string) => void;
  className?: string;
}

export const RewardCard: React.FC<RewardCardProps> = ({
  reward,
  userBalance = 0,
  showUserDKP = false,
  redemptionStatus,
  onRedeem,
  className = ''
}) => {
  const [isRedeeming, setIsRedeeming] = useState(false);

  // 计算进度百分比
  const progress = userBalance > 0 ? Math.round((userBalance / reward.dkp_cost) * 100) : 0;

  // 判断是否可以兑换
  const canRedeem = showUserDKP && userBalance >= reward.dkp_cost && !isRedeeming;

  const handleRedeem = async () => {
    if (!canRedeem || !onRedeem) return;

    setIsRedeeming(true);
    try {
      await onRedeem(reward.id);
    } finally {
      setIsRedeeming(false);
    }
  };

  // 获取奖励类型图标
  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'digital': return '🎮';
      case 'privilege': return '👑';
      case 'custom': return '🎁';
      default: return '🏆';
    }
  };

  // 获取图标背景色
  const getIconBackground = (type: string): string => {
    switch (type) {
      case 'digital': return 'bg-blue-100 text-blue-600';
      case 'privilege': return 'bg-purple-100 text-purple-600';
      case 'custom': return 'bg-pink-100 text-pink-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // 获取兑换状态文字
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending': return '等待审核';
      case 'approved': return '已批准';
      case 'rejected': return '已拒绝';
      case 'completed': return '已完成';
      default: return status;
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-200 transform hover:scale-105 ${className}`}>
      <div className="p-6">
        {/* 主要内容区域 */}
        <div className="flex items-center gap-4 mb-4">
          {/* 奖励图标 */}
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${getIconBackground(reward.type)}`}>
            {reward.image_url && (
              /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(reward.image_url) && !reward.image_url.startsWith('http') ? (
                <span>{reward.image_url}</span>
              ) : (
                <span>{getTypeIcon(reward.type)}</span>
              )
            )}
          </div>

          {/* 奖励标题 */}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{reward.title}</h3>
            {reward.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{reward.description}</p>
            )}
          </div>

          {/* 金币成本 */}
          <div className="text-right">
            <div className="text-2xl font-bold text-orange-500">{reward.dkp_cost}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">金币</div>
          </div>
        </div>

        {/* 条件显示：进度条或兑换按钮 */}
        {showUserDKP && (
          <div className="mb-4">
            {canRedeem ? (
              /* 显示兑换按钮 */
              <div className="flex justify-center">
                <button
                  onClick={handleRedeem}
                  disabled={isRedeeming}
                  className="px-8 py-3 rounded-xl text-lg font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 animate-pulse"
                >
                  {isRedeeming ? '兑换中...' : '🎁 兑换奖励'}
                </button>
              </div>
            ) : (
              /* 显示进度条 */
              <div>
                {/* 进度条 */}
                <div className="relative">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-6 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ease-out flex items-center justify-center text-xs font-bold ${
                        progress >= 75
                          ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white'
                          : progress >= 50
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white'
                          : progress > 0
                          ? 'bg-gradient-to-r from-orange-300 to-yellow-400 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    >
                      {progress >= 15 && `${progress}%`}
                    </div>
                  </div>

                  {/* 进度文字 */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-600 dark:text-gray-400">进度</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                      {userBalance} / {reward.dkp_cost}
                    </span>
                  </div>
                </div>
                
              </div>
            )}
          </div>
        )}

        {/* 兑换状态 */}
        {redemptionStatus && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
            <span className="text-lg mr-2">
              {redemptionStatus === 'pending' && '⏳'}
              {redemptionStatus === 'approved' && '✅'}
              {redemptionStatus === 'rejected' && '❌'}
            </span>
            <span className={`font-medium text-sm ${
              redemptionStatus === 'pending' ? 'text-yellow-600 dark:text-yellow-400' :
              redemptionStatus === 'approved' ? 'text-green-600 dark:text-green-400' :
              redemptionStatus === 'rejected' ? 'text-red-600 dark:text-red-400' :
              'text-blue-600 dark:text-blue-400'
            }`}>
              {getStatusText(redemptionStatus)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardCard;